import { FC, useState } from 'react';
import { MdKeyboardArrowDown, MdMoreHoriz } from 'react-icons/md';
import { DTB_POPUP_Z } from '@srm/ui/data-table/popup-z-index';
import { useMenuPick } from '@srm/ui/hooks/useMenuPick';
import Menu from '@srm/ui/menu';
import Modal from '@srm/ui/modal';
import Button from '@srm/ui/button';
import Input from '@srm/ui/input';
import type { IListElement } from '@srm/ui/types/IListElement';
import { IColumnSet } from './useColumnSets';

interface IColumnSetsBarProps {
    sets: IColumnSet[];
    activeId: string;
    activeSet?: IColumnSet;
    dirty: boolean;
    /** В активном наборе спрятаны столбцы с незаполненными обязательными
     *  ячейками — точка на сегменте показывает, где это чинится. */
    alert?: boolean;
    onApply: (id: string) => void;
    onReset: () => void;
    onSaveAsNew: (name: string) => void;
    onUpdateActive: () => void;
    onRenameActive: (name: string) => void;
    onDeleteActive: () => void;
}

/** Диалог имени набора (сохранить как новый / переименовать). */
const NameDialog: FC<{
    title: string;
    hint: string;
    initial: string;
    taken: string[];
    onSubmit: (name: string) => void;
    onClose: () => void;
}> = ({ title, hint, initial, taken, onSubmit, onClose }) => {
    const [name, setName] = useState(initial);
    const [error, setError] = useState('');
    const submit = () => {
        const value = name.trim();
        if (!value) return setError('Введите имя набора');
        if (taken.some((t) => t.toLowerCase() === value.toLowerCase() && t !== initial)) {
            return setError('Набор с таким именем уже есть');
        }
        onSubmit(value);
        onClose();
    };
    return (
        <Modal
            size="s"
            header={title}
            onClose={onClose}
            hasUnsavedInput={() => name !== initial}
            closeOnEscape
            footer={
                <div className="modal-dlg-buttons">
                    <Button size="sm" onClick={submit} aria-label="Сохранить набор">
                        Сохранить
                    </Button>
                    <Button size="sm" variant="light" onClick={onClose} aria-label="Отменить">
                        Отменить
                    </Button>
                </div>
            }
        >
            <p className="tdn-sets__hint">{hint}</p>
            <Input
                name="columnSetName"
                defaultValue={initial}
                placeholder="Название набора"
                autoFocus
                onChange={(e) => {
                    setName(e.currentTarget.value);
                    setError('');
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        submit();
                    }
                }}
            />
            {error && <div className="tdn-sets__error">{error}</div>}
        </Modal>
    );
};

interface ISetAction {
    label: string;
    onClick: () => void;
    danger?: boolean;
}

/** Пункты меню действий — своя вёрстка (`Menu` пакета, `contentRole="menu"`): контейнер, клавиатура, Tab, Escape и
 *  возврат фокуса на кнопку — его. */
const ActionsMenu: FC<{ actions: ISetAction[] }> = ({ actions }) => {
    const pick = useMenuPick();
    return actions.map(({ label, onClick, danger }) => (
        <button
            key={label}
            type="button"
            role="menuitem"
            className={danger ? 'tdn-sets__menuitem tdn-sets__menuitem--danger' : 'tdn-sets__menuitem'}
            onClick={pick(onClick)}
        >
            {label}
        </button>
    ));
};

/**
 * Конфигурационные наборы менять нельзя — их правка сохраняется как свой набор.
 */
const ColumnSetsBar: FC<IColumnSetsBarProps> = ({
    sets,
    activeId,
    activeSet,
    dirty,
    alert,
    onApply,
    onReset,
    onSaveAsNew,
    onUpdateActive,
    onRenameActive,
    onDeleteActive,
}) => {
    const [dialog, setDialog] = useState<'save' | 'rename' | null>(null);
    const [isActionsDialogOpen, setIsActionsDialogOpen] = useState(false);
    const [isSetsOpen, setIsSetsOpen] = useState(false);

    const own = !!activeSet?.own;
    const actions: ISetAction[] = [
        { label: 'Сохранить как новый набор…', onClick: () => setDialog('save') },
        ...(own && dirty ? [{ label: `Обновить «${activeSet.name}»`, onClick: onUpdateActive }] : []),
        ...(own ? [{ label: 'Переименовать…', onClick: () => setDialog('rename') }] : []),
        ...(!own && dirty && activeSet ? [{ label: `Вернуть «${activeSet.name}»`, onClick: onReset }] : []),
        ...(own ? [{ label: 'Удалить набор', onClick: onDeleteActive, danger: true }] : []),
    ];

    // выбор одного набора: встроенные и свои — две группы пунктов-радио, подписанные заголовками
    const setItems = [false, true].flatMap((isOwn): IListElement[] => {
        const group = sets.filter((set) => !!set.own === isOwn);
        if (!group.length) return [];
        const groupId = isOwn ? 'own' : 'built-in';
        return [
            { value: `title-${groupId}`, label: isOwn ? 'Мои наборы' : 'Встроенные', isTitle: true },
            ...group.map(
                (set): IListElement => ({
                    value: set.id,
                    label: (
                        <span className="tdn-sets__option-name">
                            {set.own && <span className="tdn-sets__own" aria-hidden />}
                            {set.name}
                        </span>
                    ),
                    radio: true,
                    group: groupId,
                    checked: set.id === activeId,
                    handler: () => onApply(set.id),
                }),
            ),
        ];
    });

    const openSets = () => {
        setIsActionsDialogOpen(false);
        setIsSetsOpen(true);
    };
    const openActions = () => {
        setIsSetsOpen(false);
        setIsActionsDialogOpen(true);
    };

    return (
        <div className="tdn-sets" role="group" aria-label="Наборы колонок">
            <Menu
                className="tdn-sets__select-menu"
                list={setItems}
                opened={isSetsOpen}
                onOpenChange={(opened) => (opened ? openSets() : setIsSetsOpen(false))}
                position="bottom-start"
                popoverProps={{ minWidth: 230, verticalOffset: 6, zIndex: DTB_POPUP_Z }}
            >
                <button
                    type="button"
                    className="tdn-sets__select"
                    aria-label={`Выбрать набор: ${activeSet?.name ?? 'Не выбран'}`}
                    title={alert ? 'В наборе скрыты столбцы с незаполненными обязательными полями' : activeSet?.name}
                >
                    <span className="tdn-sets__prefix">Набор:</span>
                    {own && <span className="tdn-sets__own" aria-hidden />}
                    <span className="tdn-sets__name">{activeSet?.name ?? 'Не выбран'}</span>
                    {alert && <span className="tdn-sets__alert" aria-hidden />}
                    <MdKeyboardArrowDown size={16} aria-hidden />
                </button>
            </Menu>

            <Menu
                content={<ActionsMenu actions={actions} />}
                contentRole="menu"
                opened={isActionsDialogOpen}
                onOpenChange={(opened) => (opened ? openActions() : setIsActionsDialogOpen(false))}
                position="bottom-end"
                popoverProps={{ minWidth: 230, zIndex: DTB_POPUP_Z, listClassName: 'tdn-sets__menu' }}
            >
                <button
                    type="button"
                    className={dirty ? 'tdn-sets__more tdn-sets__more--dirty' : 'tdn-sets__more'}
                    aria-label={dirty ? 'Действия с набором (есть несохранённые изменения)' : 'Действия с набором'}
                    title={dirty ? 'Набор изменён — действия с набором' : 'Действия с набором'}
                >
                    <MdMoreHoriz size="16" />
                </button>
            </Menu>

            {dialog === 'save' && (
                <NameDialog
                    title="Сохранить набор колонок"
                    hint="В набор войдёт текущий выбор колонок. Он будет виден только вам."
                    initial=""
                    taken={sets.map((s) => s.name)}
                    onSubmit={onSaveAsNew}
                    onClose={() => setDialog(null)}
                />
            )}
            {dialog === 'rename' && activeSet && (
                <NameDialog
                    title="Переименовать набор"
                    hint="Новое имя набора."
                    initial={activeSet.name}
                    taken={sets.map((s) => s.name)}
                    onSubmit={onRenameActive}
                    onClose={() => setDialog(null)}
                />
            )}
        </div>
    );
};

export default ColumnSetsBar;
